import { InfoOutlined } from '@mui/icons-material';
import { Box, Container, Stack, styled } from '@mui/material';
import { Link } from 'react-router';

import routes from '@/assets/constants/routes-no.ts';
import { MedlemsfordelRibbon } from '@/assets/images/medlemsfordel-ribbon.tsx';
import { WinnerQuotePhoto } from '@/competitions/_common/components/WinnerQuotePhoto.tsx';
import { LoanExampleText } from '@/components/LoanExampleText/index.ts';
import Page from '@/components/Page/index.ts';
import { UnderlinedText } from '@/components/UnderlinedText/index.ts';
import { competitionWinnersQuery } from '@/lib/api/requests.ts';
import { getQueryClient } from '@/lib/query-client-context.ts';
import useBrainSession from '@/services/BrainSession/useBrainSession.ts';
import { Button } from '@/ui/Button.tsx';

import type { Route } from './+types/LandingPage';
import runeBakken from './rune-bakken.webp';

const Root = styled('div')(({ theme }) => ({
  background: theme.palette.background.contrast,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: theme.spacing(10),
  padding: theme.spacing(7, 0),
  color: 'white',

  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(15, 0),
  },
}));

const Card = styled('div')(({ theme }) => ({
  borderRadius: '12px',
  backgroundColor: 'white',
  color: theme.palette.text.default,
  padding: theme.spacing(3),
  paddingTop: theme.spacing(5),
  textAlign: 'center',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: theme.spacing(8),

  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(5, 8),
    marginBottom: theme.spacing(10),
  },
}));

const WinnerCard = styled('div')(({ theme }) => ({
  backgroundColor: '#F0F4FA',
  padding: theme.spacing(1, 2),
}));

const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.text.default,
}));

const CustomContainer = styled('div')(({ theme }) => ({
  '--gutter': '16px',

  maxWidth: 'min(700px, calc(100% - 2 * var(--gutter)))',

  [theme.breakpoints.up('sm')]: {
    '--gutter': '24px',
  },
}));

export async function loader({ context }: Route.LoaderArgs) {
  const queryClient = getQueryClient(context);
  const competitions = await queryClient.query(competitionWinnersQuery());
  const competition = competitions.find((c) => c.slug === 'april_2024_campaign');
  return {
    winner: competition?.winners[0],
  };
}

export default function LandingPage({ loaderData }: Route.ComponentProps) {
  const { toggleChat } = useBrainSession();
  const winner = loaderData.winner;

  return (
    <Page title="April-trekning">
      <Root>
        <CustomContainer>
          <Card>
            <MedlemsfordelRibbon
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                transform: {
                  xs: 'translate(-37%, -37%) scale(0.6)',
                  sm: 'translate(-28%, -28%)',
                },
              }}
            />

            <h1 className="mb-4 max-w-[25ch] typography-v1-h2 text-balance">
              Vinn inntil <UnderlinedText>100 000 kr</UnderlinedText> til nedbetaling av
              forbruksgjeld
            </h1>

            <p className="mb-1 typography-v1-body3">Denne trekningen er avsluttet</p>

            {winner && (
              <WinnerCard className="mb-3 typography-v1-h4">
                Vinner: {winner.name} - {winner.age} - {winner.city}
              </WinnerCard>
            )}

            <p className="mb-3 typography-v1-body4">
              Som Scoopr-medlem kunne du delta om å vinne inntil 100 000 kr til nedbetaling av
              forbruksgjeld. Vinneren vant en nedbetaling på forbruksgjeld på 100 000 kr. Vi vil at
              enda flere skal sjekke om de kan få lavere rente.
            </p>

            <Button
              color="yellow"
              sx={{ mb: 1.5 }}
              onClick={() =>
                toggleChat({
                  goal: 'debt_check',
                  step: 'skip_start',
                })
              }
            >
              Søk om lavere rente
            </Button>

            <Stack
              direction="row"
              sx={{
                gap: 1,
                mb: 2,
              }}
            >
              <StyledLink className="typography-v1-body4" to={routes.APRIL_2024_COMPETITION_TERMS}>
                Vilkår og Betingelser
              </StyledLink>
              <InfoOutlined fontSize="inherit" />
            </Stack>

            <div className="typography-v1-body4 text-[#898989]">
              <LoanExampleText />
            </div>
          </Card>
        </CustomContainer>

        <Container>
          <Box sx={{ mb: 8 }}>
            <WinnerQuotePhoto
              photo={runeBakken}
              photoAlt="Rune Bakken (36)"
              photoCaption="Foto: Thomas Rasmus Skaug / Dagbladet"
              quote="- Jeg hadde en forhåpning om at jeg kunne få lavere rente, men jeg hadde aldri trodd jeg skulle være så heldig at jeg vant."
              quoteCaption="Sier Rune Bakken (36), vinner av April-trekningen, til Dagbladet"
              link="https://www.dagbladet.no/annonse/rune-vant-100-000-kroner-ved-a-sjekke-gjelden-sin/81381960"
            />
          </Box>
        </Container>

        <CustomContainer>
          <Box sx={{ textAlign: 'center' }}>
            <h2 className="mb-2 typography-v1-h2">Om Scoopr</h2>

            <p className="mb-5 typography-v1-body4">
              Vi mener at <strong>alle bør sjekke om de kan få lavere</strong> rente på sine
              forbrukslån og kredittkort. Det kan nemlig spare deg for tusenvis av kroner.
              <br />
              <br />
              Vi vil <strong>ikke</strong> at du skal ta opp <strong>mere lån</strong>. Vi vil at du
              skal <strong>fikse</strong> de <strong>lånene du har</strong>, og spare penger.
            </p>

            <h3 className="mb-4 typography-v1-h3">Dette klarte vi i 2023</h3>
          </Box>
        </CustomContainer>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          divider={
            <Box
              sx={{
                '--thickness': '2px',
                backgroundColor: '#CEE2FF',
                width: { xs: '100%', sm: 'var(--thickness)' },
                height: { xs: 'var(--thickness)', sm: 'auto' },
                alignSelf: 'stretch',
              }}
            />
          }
          sx={{
            gap: { xs: 2, md: 8 },
            color: '#F0F4FA',
          }}
        >
          <Stat value="229 000+" label="Sjekket renten med Scoopr" />
          <Stat value="147 MNOK" label="Kuttet rentekostnader på kredittkort" />
          <Stat value="103 MNOK" label="Kuttet rentekostnader på forbrukslån" />
        </Stack>
      </Root>
    </Page>
  );
}

type StatProps = {
  label: string;
  value: string;
};

const Stat: React.FC<StatProps> = ({ label, value }) => {
  return (
    <Stack sx={{ textAlign: 'center' }}>
      <p className="typography-v1-h1">{value}</p>
      <p className="typography-v1-body4">{label}</p>
    </Stack>
  );
};
